import { canEdit } from '../store.js';
import { getContext } from '../trip-context.js';
import { escapeHtml } from '../utils/dom.js';
import { isGeminiConfigured, sendGeminiChat } from '../utils/gemini-client.js';
import { buildTripAiContext, buildTripAiSystemPrompt } from '../utils/trip-ai-context.js';
import { analyzePlaceQuestion } from '../utils/trip-ai-place-lookup.js';
import {
  buildQuickChips,
  buildWelcomeMessage,
  shouldUseWebSearch,
} from '../utils/trip-ai-routing.js';
import { trapModalHistory } from '../utils/modal-history.js';

const STORAGE_PREFIX = 'metis-ai-chat:';
const WEB_SEARCH_PREFIX = 'metis-ai-chat:web:';

/** @type {AbortController | null} */
let activeRequest = null;
/** @type {(() => void) | null} */
let panelDismiss = null;
/** @type {HTMLElement | null} */
let panelContainerRef = null;

function storageKey(instanceId) {
  return `${STORAGE_PREFIX}${instanceId}`;
}

function webSearchKey(instanceId) {
  return `${WEB_SEARCH_PREFIX}${instanceId}`;
}

function loadWebSearchPreference(instanceId) {
  try {
    return localStorage.getItem(webSearchKey(instanceId)) === '1';
  } catch {
    return false;
  }
}

function saveWebSearchPreference(instanceId, enabled) {
  localStorage.setItem(webSearchKey(instanceId), enabled ? '1' : '0');
}

function loadMessages(instanceId) {
  try {
    const raw = localStorage.getItem(storageKey(instanceId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveMessages(instanceId, messages) {
  localStorage.setItem(storageKey(instanceId), JSON.stringify(messages));
}

function formatAssistantBody(text) {
  const escaped = escapeHtml(text);
  return escaped
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\n/g, '<br>');
}

function renderSources(sources) {
  if (!sources?.length) return '';
  const links = sources
    .slice(0, 4)
    .map(
      (source) =>
        `<a href="${escapeHtml(source.uri)}" target="_blank" rel="noopener noreferrer">${escapeHtml(source.title)}</a>`,
    )
    .join(' · ');
  return `<div class="trip-ai-chat__sources">Fonti: ${links}</div>`;
}

function renderMessage(message, index, { loading = false } = {}) {
  const roleClass = message.role === 'user' ? 'trip-ai-chat__msg--user' : 'trip-ai-chat__msg--assistant';
  const label = message.role === 'user' ? 'Tu' : 'Nicolò';
  const body =
    message.role === 'assistant'
      ? formatAssistantBody(message.content)
      : escapeHtml(message.content);
  const sources = message.role === 'assistant' ? renderSources(message.sources) : '';
  const retryBtn = loading
    ? ''
    : message.role === 'user'
      ? `<button type="button" class="trip-ai-chat__retry" data-retry-user="${index}" title="Reinvia e rigenera risposta" aria-label="Reinvia messaggio">↻</button>`
      : `<button type="button" class="trip-ai-chat__retry trip-ai-chat__retry--assistant" data-retry-assistant="${index}" title="Rigenera risposta" aria-label="Rigenera risposta">↻ Rigenera</button>`;

  return `
    <div class="trip-ai-chat__msg ${roleClass}">
      <div class="trip-ai-chat__msg-head">
        <span class="trip-ai-chat__msg-label">${label}</span>
        ${message.role === 'user' ? retryBtn : ''}
      </div>
      <div class="trip-ai-chat__msg-body">${body}</div>
      ${sources}
      ${message.role === 'assistant' ? retryBtn : ''}
    </div>
  `;
}

function renderMessagesHtml(messages, { loading = false, error = '', welcomeMessage = '', loadingWeb = false } = {}) {
  const rows = messages.length
    ? messages.map((message, index) => renderMessage(message, index, { loading })).join('')
    : `<div class="trip-ai-chat__msg trip-ai-chat__msg--assistant">
        <span class="trip-ai-chat__msg-label">Nicolò</span>
        <div class="trip-ai-chat__msg-body">${escapeHtml(welcomeMessage)}</div>
      </div>`;

  const loadingText = loadingWeb ? 'Sto cercando online…' : 'Sto pensando…';
  const loadingRow = loading
    ? `<div class="trip-ai-chat__msg trip-ai-chat__msg--assistant trip-ai-chat__msg--loading" aria-live="polite">
        <span class="trip-ai-chat__msg-label">Nicolò</span>
        <div class="trip-ai-chat__msg-body"><span class="trip-ai-chat__typing" aria-hidden="true"></span> ${loadingText}</div>
      </div>`
    : '';

  const errorRow = error
    ? `<div class="trip-ai-chat__error" role="alert">
        <span>${escapeHtml(error)}</span>
        <button type="button" class="trip-ai-chat__retry trip-ai-chat__retry--error" data-retry-last>Riprova</button>
      </div>`
    : '';

  return `${rows}${loadingRow}${errorRow}`;
}

function renderQuickChips(chips) {
  if (!chips.length) return '';
  const buttons = chips
    .map(
      (chip) =>
        `<button type="button" class="trip-ai-chat__chip" data-chip-text="${escapeHtml(chip.text)}"${chip.webSearch ? ' data-chip-web="1"' : ''}>${escapeHtml(chip.label)}</button>`,
    )
    .join('');
  return `<div class="trip-ai-chat__chips" role="group" aria-label="Domande rapide">${buttons}</div>`;
}

function scrollMessagesToEnd(container) {
  const list = container.querySelector('#trip-ai-chat-messages');
  if (!list) return;
  list.scrollTop = list.scrollHeight;
}

function setPanelOpen(container, open) {
  const panel = container.querySelector('#trip-ai-chat');
  const fab = container.querySelector('#trip-ai-chat-fab');
  if (!panel || !fab) return;

  if (open) {
    panel.hidden = false;
    fab.setAttribute('aria-expanded', 'true');
    panelContainerRef = container;
    if (!panelDismiss) {
      panelDismiss = trapModalHistory(closePanelFromBack);
    }
    const input = panel.querySelector('#trip-ai-chat-input');
    window.setTimeout(() => input?.focus(), 120);
    scrollMessagesToEnd(container);
    return;
  }

  if (panelDismiss) {
    const dismiss = panelDismiss;
    panelDismiss = null;
    panelContainerRef = null;
    dismiss();
    return;
  }

  panel.hidden = true;
  fab.setAttribute('aria-expanded', 'false');
  panelContainerRef = null;
}

function closePanelFromBack() {
  const container = panelContainerRef;
  panelDismiss = null;
  panelContainerRef = null;
  const panel = container?.querySelector('#trip-ai-chat');
  const fab = container?.querySelector('#trip-ai-chat-fab');
  if (panel) panel.hidden = true;
  fab?.setAttribute('aria-expanded', 'false');
}

function refreshMessagesView(container, state) {
  const list = container.querySelector('#trip-ai-chat-messages');
  if (!list) return;
  list.innerHTML = renderMessagesHtml(state.messages, {
    loading: state.loading,
    error: state.error,
    welcomeMessage: state.welcomeMessage,
    loadingWeb: state.loadingWeb,
  });
  scrollMessagesToEnd(container);
}

function syncWebToggle(container, instanceId) {
  const toggle = container.querySelector('#trip-ai-chat-web');
  if (!toggle || !instanceId) return;
  const enabled = loadWebSearchPreference(instanceId);
  toggle.setAttribute('aria-pressed', enabled ? 'true' : 'false');
  toggle.classList.toggle('trip-ai-chat__web-toggle--on', enabled);
}

function renderSetupNotice() {
  return `
    <div class="trip-ai-chat__setup">
      <p>Per usare Nicolò aggiungi <code>VITE_GEMINI_API_KEY</code> nel file <code>.env</code>.</p>
      <p>Chiave gratuita su <a href="https://aistudio.google.com/apikey" target="_blank" rel="noopener noreferrer">Google AI Studio</a>. Se compare «limite 0», collega il billing al progetto (senza costi entro i limiti free).</p>
    </div>
  `;
}

export function renderTripAiChat() {
  if (!canEdit()) return '';

  const configured = isGeminiConfigured();
  const ctx = getContext();
  const subtitle = ctx?.trip?.title?.trim() || ctx?.trip?.country?.trim() || 'Il tuo compagno di viaggio';

  return `
    <button
      type="button"
      class="trip-ai-chat-fab"
      id="trip-ai-chat-fab"
      aria-label="Apri Nicolò"
      aria-expanded="false"
      aria-controls="trip-ai-chat"
    >
      <span class="trip-ai-chat-fab__icon" aria-hidden="true">✨</span>
    </button>
    <div class="trip-ai-chat" id="trip-ai-chat" hidden>
      <button type="button" class="trip-ai-chat__backdrop" id="trip-ai-chat-backdrop" aria-label="Chiudi assistente"></button>
      <div class="trip-ai-chat__panel" role="dialog" aria-modal="true" aria-labelledby="trip-ai-chat-title">
        <div class="trip-ai-chat__handle" aria-hidden="true"></div>
        <header class="trip-ai-chat__header">
          <div>
            <h2 class="trip-ai-chat__title" id="trip-ai-chat-title">Nicolò</h2>
            <p class="trip-ai-chat__subtitle">${escapeHtml(subtitle)}</p>
          </div>
          <button type="button" class="trip-ai-chat__clear" id="trip-ai-chat-clear" title="Cancella conversazione">🗑</button>
        </header>
        ${configured ? '' : renderSetupNotice()}
        <div class="trip-ai-chat__messages" id="trip-ai-chat-messages" aria-live="polite"></div>
        <div id="trip-ai-chat-chips"></div>
        <form class="trip-ai-chat__form" id="trip-ai-chat-form">
          <button
            type="button"
            class="trip-ai-chat__web-toggle"
            id="trip-ai-chat-web"
            aria-pressed="false"
            title="Cerca anche online (forza ricerca web)"
            ${configured ? '' : 'disabled'}
          >
            <span aria-hidden="true">🌐</span>
          </button>
          <label class="visually-hidden" for="trip-ai-chat-input">Messaggio</label>
          <textarea
            id="trip-ai-chat-input"
            class="trip-ai-chat__input"
            rows="1"
            placeholder="Es. Il Kozjak c'è? / Consigli per domani?"
            enterkeyhint="send"
            ${configured ? '' : 'disabled'}
          ></textarea>
          <button type="submit" class="trip-ai-chat__send" ${configured ? '' : 'disabled'} aria-label="Invia messaggio">
            <svg class="trip-ai-chat__send-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M22 2 11 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M22 2 15 22 11 13 2 9 22 2z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span class="trip-ai-chat__send-label">Invia</span>
          </button>
        </form>
      </div>
    </div>
  `;
}

async function generateReply(container, form, { instanceId, messages, userText, forceWebSearch = false }) {
  const ctx = getContext();
  const input = form.querySelector('#trip-ai-chat-input');
  const sendBtn = form.querySelector('.trip-ai-chat__send');
  const welcomeMessage = buildWelcomeMessage(ctx);

  const webForced = forceWebSearch || loadWebSearchPreference(instanceId);
  let placeAnalysis = null;
  try {
    placeAnalysis = await analyzePlaceQuestion(userText, ctx);
  } catch {
    placeAnalysis = { verdict: 'general' };
  }

  const googleSearch = shouldUseWebSearch({
    message: userText,
    placeAnalysis,
    forceWebSearch: webForced,
  });

  refreshMessagesView(container, {
    messages,
    loading: true,
    error: '',
    welcomeMessage,
    loadingWeb: googleSearch,
  });
  if (input) input.disabled = true;
  if (sendBtn) sendBtn.disabled = true;

  activeRequest?.abort();
  activeRequest = new AbortController();

  try {
    const contextPayload = buildTripAiContext(ctx);
    if (!contextPayload) throw new Error('Contesto viaggio non disponibile');

    const { text: reply, sources } = await sendGeminiChat({
      systemPrompt: buildTripAiSystemPrompt(contextPayload, placeAnalysis, { webSearchEnabled: googleSearch }),
      messages,
      signal: activeRequest.signal,
      googleSearch,
    });

    messages.push({
      role: 'assistant',
      content: reply,
      ...(sources.length ? { sources } : {}),
    });
    saveMessages(instanceId, messages);
    refreshMessagesView(container, {
      messages,
      loading: false,
      error: '',
      welcomeMessage,
    });
  } catch (err) {
    if (err?.name === 'AbortError') return;
    refreshMessagesView(container, {
      messages,
      loading: false,
      error: err?.message ?? 'Errore durante la richiesta',
      welcomeMessage,
    });
  } finally {
    if (input) input.disabled = false;
    if (sendBtn) sendBtn.disabled = false;
    activeRequest = null;
    input?.focus();
  }
}

function findLastUserIndex(messages) {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    if (messages[index]?.role === 'user') return index;
  }
  return -1;
}

async function retryUserMessage(container, form, userIndex, { forceWebSearch = false } = {}) {
  const ctx = getContext();
  const instanceId = ctx?.instance?.id;
  if (!instanceId || !isGeminiConfigured() || userIndex < 0) return;

  const messages = loadMessages(instanceId).slice(0, userIndex + 1);
  const userText = messages[userIndex]?.content;
  if (!userText) return;

  saveMessages(instanceId, messages);
  await generateReply(container, form, { instanceId, messages, userText, forceWebSearch });
}

async function handleSubmit(container, form, { forceWebSearch = false } = {}) {
  const ctx = getContext();
  const instanceId = ctx?.instance?.id;
  if (!instanceId || !isGeminiConfigured()) return;

  const input = form.querySelector('#trip-ai-chat-input');
  const text = input?.value?.trim();
  if (!text) return;

  const messages = loadMessages(instanceId);
  messages.push({ role: 'user', content: text });
  saveMessages(instanceId, messages);
  if (input) input.value = '';

  const webForced = forceWebSearch || loadWebSearchPreference(instanceId);
  await generateReply(container, form, {
    instanceId,
    messages,
    userText: text,
    forceWebSearch: webForced,
  });
}

function bindMessageRetries(container, form) {
  const list = container.querySelector('#trip-ai-chat-messages');
  if (!list || list.dataset.retryBound === '1') return;
  list.dataset.retryBound = '1';

  list.addEventListener('click', (event) => {
    const target = event.target.closest('[data-retry-user], [data-retry-assistant], [data-retry-last]');
    if (!target || activeRequest) return;

    if (target.hasAttribute('data-retry-last')) {
      const ctx = getContext();
      const instanceId = ctx?.instance?.id;
      if (!instanceId) return;
      const userIndex = findLastUserIndex(loadMessages(instanceId));
      retryUserMessage(container, form, userIndex);
      return;
    }

    if (target.hasAttribute('data-retry-user')) {
      const userIndex = Number.parseInt(target.getAttribute('data-retry-user'), 10);
      if (Number.isFinite(userIndex)) retryUserMessage(container, form, userIndex);
      return;
    }

    if (target.hasAttribute('data-retry-assistant')) {
      const assistantIndex = Number.parseInt(target.getAttribute('data-retry-assistant'), 10);
      if (!Number.isFinite(assistantIndex)) return;
      const ctx = getContext();
      const instanceId = ctx?.instance?.id;
      if (!instanceId) return;
      const messages = loadMessages(instanceId);
      let userIndex = assistantIndex - 1;
      while (userIndex >= 0 && messages[userIndex]?.role !== 'user') userIndex -= 1;
      if (userIndex >= 0) retryUserMessage(container, form, userIndex);
    }
  });
}

function mountQuickChips(container, ctx) {
  const chipsHost = container.querySelector('#trip-ai-chat-chips');
  if (!chipsHost || !isGeminiConfigured()) return;
  const chips = buildQuickChips(ctx);
  chipsHost.innerHTML = renderQuickChips(chips);

  chipsHost.querySelectorAll('.trip-ai-chat__chip').forEach((button) => {
    button.addEventListener('click', () => {
      const form = container.querySelector('#trip-ai-chat-form');
      const input = form?.querySelector('#trip-ai-chat-input');
      const chipText = button.getAttribute('data-chip-text');
      if (!input || !chipText || !form) return;
      input.value = chipText;

      if (button.hasAttribute('data-chip-web')) {
        handleSubmit(container, form, { forceWebSearch: true });
        return;
      }

      form.requestSubmit();
    });
  });
}

export function bindTripAiChat(container) {
  if (!canEdit()) return;

  const fab = container.querySelector('#trip-ai-chat-fab');
  const backdrop = container.querySelector('#trip-ai-chat-backdrop');
  const form = container.querySelector('#trip-ai-chat-form');
  const clearBtn = container.querySelector('#trip-ai-chat-clear');
  const webToggle = container.querySelector('#trip-ai-chat-web');
  const ctx = getContext();
  const instanceId = ctx?.instance?.id;
  const welcomeMessage = buildWelcomeMessage(ctx);

  if (instanceId) {
    syncWebToggle(container, instanceId);
    refreshMessagesView(container, {
      messages: loadMessages(instanceId),
      loading: false,
      error: '',
      welcomeMessage,
    });
  }

  mountQuickChips(container, ctx);
  bindMessageRetries(container, form);

  fab?.addEventListener('click', () => {
    const panel = container.querySelector('#trip-ai-chat');
    const isOpen = panel && !panel.hidden;
    setPanelOpen(container, !isOpen);
  });

  backdrop?.addEventListener('click', () => setPanelOpen(container, false));

  webToggle?.addEventListener('click', () => {
    if (!instanceId) return;
    const next = !loadWebSearchPreference(instanceId);
    saveWebSearchPreference(instanceId, next);
    syncWebToggle(container, instanceId);
  });

  form?.addEventListener('submit', (event) => {
    event.preventDefault();
    handleSubmit(container, form);
  });

  const input = form?.querySelector('#trip-ai-chat-input');
  input?.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' || event.shiftKey || event.isComposing) return;
    event.preventDefault();
    form?.requestSubmit();
  });

  clearBtn?.addEventListener('click', () => {
    if (!instanceId) return;
    if (!window.confirm('Cancellare tutta la conversazione?')) return;
    localStorage.removeItem(storageKey(instanceId));
    refreshMessagesView(container, { messages: [], loading: false, error: '', welcomeMessage });
  });
}

export function cancelTripAiChatRequest() {
  activeRequest?.abort();
  activeRequest = null;
  if (panelDismiss) {
    panelDismiss.cancel?.();
    panelDismiss = null;
  }
  panelContainerRef = null;
}
